import { memo, useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist/build/pdf.mjs';
import { usePdf } from '../store/pdfStore.js';
import { useConfig } from '../store/configStore.js';

const itemCache = new Map();

// Map a CSS-ish font family string to one of the 3 standard PDF families,
// also detecting bold / italic from the family name.
function mapFontFamily(fam) {
  if (!fam) return { family: 'Helvetica', bold: false, italic: false };
  const f = fam.toLowerCase();
  const bold = /bold|black|heavy|semibold|[-_ ]bd\b/.test(f);
  const italic = /italic|oblique/.test(f);
  let family = 'Helvetica';
  if (/times|georgia|serif/.test(f) && !/sans/.test(f)) family = 'Times';
  else if (/courier|mono|consol/.test(f)) family = 'Courier';
  return { family, bold, italic };
}

const toHex = (c) => c.toString(16).padStart(2, '0');

// Sample ink + background colour of a glyph from the rendered page
// bitmap. Inside the glyph box: the darkest pixel is the text stroke
// (ink), the lightest is the page background showing between letters.
// Sampling the background lets the override's mask blend into a
// non-white page instead of leaving a visible white rectangle.
function sampleColors(img, left, top, w, h) {
  const { data, width, height } = img;
  const x0 = Math.max(0, Math.floor(left));
  const x1 = Math.min(width - 1, Math.ceil(left + w));
  const y0 = Math.max(0, Math.floor(top));
  const y1 = Math.min(height - 1, Math.ceil(top + h));
  let darkest = null;
  let darkLum = 256;
  let lightest = null;
  let lightLum = -1;
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      if (lum < darkLum) {
        darkLum = lum;
        darkest = [r, g, b];
      }
      if (lum > lightLum) {
        lightLum = lum;
        lightest = [r, g, b];
      }
    }
  }
  const ink = darkest && darkLum <= 225
    ? `#${toHex(darkest[0])}${toHex(darkest[1])}${toHex(darkest[2])}`
    : '#111111';
  const bg = lightest
    ? `#${toHex(lightest[0])}${toHex(lightest[1])}${toHex(lightest[2])}`
    : '#ffffff';
  return { ink, bg };
}

async function getItems(doc, originalIndex, scale) {
  const key = `${doc.fingerprints?.[0] || 'doc'}:${originalIndex}:${scale}`;
  if (itemCache.has(key)) return itemCache.get(key);
  const page = await doc.getPage(originalIndex + 1);
  const viewport = page.getViewport({ scale });
  const content = await page.getTextContent({ includeMarkedContent: false, disableNormalization: true });
  const styles = content.styles || {};

  // Render the page once to sample real glyph colours.
  let img = null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  } catch {
    img = null;
  }

  const items = [];
  for (let idx = 0; idx < content.items.length; idx++) {
    const it = content.items[idx];
    if (!it.str || !it.str.trim()) continue;
    const tx = pdfjs.Util.transform(viewport.transform, it.transform);
    const fontHeight = Math.hypot(tx[2], tx[3]);
    const width = it.width * scale;
    const left = tx[4];
    const top = tx[5] - fontHeight;
    const style = styles[it.fontName];
    const fm = mapFontFamily(style && style.fontFamily);
    const cols = img
      ? sampleColors(img, left, top, width, fontHeight)
      : { ink: '#111111', bg: '#ffffff' };
    items.push({
      itemKey: `p${originalIndex}_${idx}`,
      str: it.str,
      left,
      top,
      width,
      height: fontHeight,
      fontSize: fontHeight,
      font: fm.family,
      bold: fm.bold,
      italic: fm.italic,
      color: cols.ink,
      bg: cols.bg,
    });
  }
  itemCache.set(key, items);
  return items;
}

export function clearTextItemCache() {
  itemCache.clear();
}

function TextLayerBase({ doc, page, scale }) {
  const tool = usePdf((s) => s.tool);
  const anns = usePdf((s) => s.annotations[page.id]) || [];
  const addAnnotation = usePdf((s) => s.addAnnotation);
  const textSelection = usePdf((s) => s.textSelection);
  const toggleTextSelection = usePdf((s) => s.toggleTextSelection);
  const moveTextSelection = usePdf((s) => s.moveTextSelection);
  const beginEdit = usePdf((s) => s.beginEdit);
  const editorDefaults = useConfig((s) => s.config.settings.editor);
  const [items, setItems] = useState([]);
  const dragRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!doc) return;
    getItems(doc, page.originalIndex, scale).then((list) => {
      if (alive) setItems(list);
    });
    return () => {
      alive = false;
    };
  }, [doc, page.originalIndex, scale]);

  const editedKeys = new Set(
    anns.filter((a) => a.type === 'text' && a.originalItemKey).map((a) => a.originalItemKey)
  );

  const editable = tool === 'select';

  const entryOf = (it) => ({
    pageId: page.id,
    itemKey: it.itemKey,
    x: it.left / scale,
    y: it.top / scale,
    w: it.width / scale,
    h: it.height / scale,
    fontSize: it.fontSize / scale,
    font: it.font,
    bold: it.bold,
    italic: it.italic,
    color: it.color,
    bg: it.bg,
    str: it.str,
  });

  const onItemMouseDown = (e, it) => {
    if (!editable) return;
    e.stopPropagation();
    const additive = e.ctrlKey || e.metaKey || e.shiftKey;
    const selKey = `${page.id}::${it.itemKey}`;
    const alreadySelected = textSelection.has(selKey);

    // Already-selected glyph (and not an additive click): start a drag.
    if (alreadySelected && !additive) {
      dragRef.current = { mx: e.clientX, my: e.clientY, moved: false };
      const onMove = (ev) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = (ev.clientX - d.mx) / scale;
        const dy = (ev.clientY - d.my) / scale;
        if (!d.moved && Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
        if (!d.moved) {
          d.moved = true;
          beginEdit(); // single undo step for the whole drag
        }
        moveTextSelection(dx, dy);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        const moved = dragRef.current?.moved;
        dragRef.current = null;
        // No movement -> treat as a plain click (toggle selection off).
        if (!moved) toggleTextSelection(entryOf(it), false);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return;
    }

    toggleTextSelection(entryOf(it), additive);
  };

  const selectItem = (it) => {
    toggleTextSelection(entryOf(it), false);
  };

  const startEdit = (it) => {
    // If this glyph already has an override annotation, just re-select it
    // instead of stacking a duplicate.
    if (editedKeys.has(it.itemKey)) {
      selectItem(it);
      return;
    }
    // Inherit the glyph's own style so editing the text content does
    // not silently restyle it.
    const ann = {
      id: crypto.randomUUID(),
      type: 'text',
      originalItemKey: it.itemKey,
      _edit: true,
      x: it.left / scale,
      y: it.top / scale,
      w: it.width / scale,
      h: it.height / scale,
      size: it.fontSize / scale,
      font: it.font || editorDefaults.defaultFont,
      bold: !!it.bold,
      italic: !!it.italic,
      color: it.color || '#111111',
      background: it.bg || '#ffffff',
      text: it.str,
    };
    addAnnotation(page.id, ann);
  };

  return (
    <div className={`text-layer ${editable ? 'editable' : ''}`}>
      {items.map((it) => {
        // Glyphs that already have a style override are owned by their
        // bound annotation in the AnnotationLayer (selectable + draggable
        // there); don't render a hit-area for them here.
        if (editedKeys.has(it.itemKey)) return null;
        const selKey = `${page.id}::${it.itemKey}`;
        const isSelected = textSelection.has(selKey);
        return (
          <span
            key={it.itemKey}
            className={`text-item ${isSelected ? 'selected' : ''}`}
            style={{
              left: it.left,
              top: it.top,
              width: it.width,
              height: it.height,
              fontSize: it.fontSize,
              lineHeight: `${it.height}px`,
            }}
            title={editable ? it.str : undefined}
            onMouseDown={(e) => onItemMouseDown(e, it)}
            onDoubleClick={(e) => {
              if (!editable) return;
              e.stopPropagation();
              startEdit(it);
            }}
          >
            {it.str}
          </span>
        );
      })}
    </div>
  );
}

export const TextLayer = memo(TextLayerBase);
